import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
    Form, FormField, FormItem, FormLabel, FormControl, FormMessage,
} from '@/components/ui/form';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { netBankingSchema } from '../utils/validation';
import { POPULAR_BANKS, ALL_BANKS } from '../types/index';

/**
 * Net Banking payment form with popular bank shortcuts + full dropdown.
 * @param {{ onSubmit: Function, isProcessing: boolean }} props
 */
export function NetBankingForm({ onSubmit, isProcessing }) {
    const form = useForm({
        resolver: zodResolver(netBankingSchema),
        defaultValues: { bankId: '' },
        mode: 'onTouched',
    });

    return (
        <div className="space-y-5" data-testid="net-banking-form">
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} noValidate className="space-y-5" aria-label="Net banking form">
                    <FormField
                        control={form.control}
                        name="bankId"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Select Your Bank</FormLabel>

                                {/* Popular banks grid */}
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
                                    {POPULAR_BANKS.map((bank) => (
                                        <button
                                            key={bank.id}
                                            type="button"
                                            onClick={() => {
                                                field.onChange(bank.id);
                                                form.trigger('bankId');
                                            }}
                                            data-testid={`bank-${bank.id}`}
                                            aria-pressed={field.value === bank.id}
                                            className={`flex items-center gap-2 p-3 border rounded-sm text-sm font-medium transition-all duration-200
                        ${field.value === bank.id
                                                    ? 'border-[#F87217] bg-[#F87217]/5 text-[#F87217]'
                                                    : 'border-slate-200 text-slate-600 hover:border-[#F87217]/50 hover:bg-slate-50'
                                                }`}
                                        >
                                            <span aria-hidden="true">{bank.logo}</span>
                                            {bank.name}
                                        </button>
                                    ))}
                                </div>

                                <Separator className="my-4" />

                                {/* Other banks dropdown */}
                                <Select
                                    value={field.value}
                                    onValueChange={(v) => { field.onChange(v); form.trigger('bankId'); }}
                                >
                                    <SelectTrigger aria-label="Select other bank" data-testid="select-bank">
                                        <SelectValue placeholder="Or choose another bank..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {ALL_BANKS.map((bank) => (
                                            <SelectItem key={bank.id} value={bank.id}>{bank.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>

                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <Button
                        type="submit"
                        disabled={isProcessing}
                        className="w-full bg-[#F87217] hover:bg-[#C65B12] text-white rounded-sm btn-primary-glow font-semibold py-6"
                        data-testid="submit-net-banking"
                    >
                        {isProcessing ? (
                            <span className="flex items-center gap-2">
                                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                Redirecting to Bank...
                            </span>
                        ) : 'Proceed to Net Banking'}
                    </Button>
                </form>
            </Form>
        </div>
    );
}

export default NetBankingForm;
